import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Checkbox, Dialog, FormControlLabel, makeStyles, RadioGroup } from '@material-ui/core';
import moment from 'moment';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { CloseIcon, DateIcon } from '../../../../../components/Icon';
import { AutoCompleteSelect } from '../../../../../components';
import StyleButton from '../../../../../components/StyleButton';
import LongRemark from '../../../../../components/LongRemark';
import useServiceHoursCheckRules from '../hooks/useServiceHoursCheckRules';
import { teleVisitAPI } from '../../../../../apis';
import { i18n } from '../../../../../language';
import { KeyboardDatePicker } from '../../../../../components/DatePicker';
import TimeInput from './Field/TimeInput';
import AddServiceHoursConfirmation from './AddServiceHoursConfirmation';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiDialog-paperWidthSm': {
      maxWidth: 'none',
    },
    '& .MuiPaper-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '15px !important',
      boxShadow: '0 -0.125rem 1.3125rem rgba(30, 56, 140, 0.18)',
      margin: '0',
      paddingTop: '2.722rem',
      width: '44.375rem',
    },
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    color: '#2e4469',
  },
  subTitle: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.875rem',
    color: '#7d86a9',
  },
  closeBtn: {
    width: '1.945rem',
    height: '1.945rem',
  },
  closeIcon: {
    fontSize: '1.5rem',
    color: '#538fff',
  },
  input: {
    width: '100%',
    height: '3.5rem',
    borderRadius: '10px',
    border: 'solid 1px #e4e7eb',
    paddingLeft: '1.25rem',
    paddingRight: '1.188rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  inputTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#666666',
  },
  date: () => ({
    '& .MuiInputBase-root': {
      borderRadius: '10px',
      backgroundColor: '#ffffff',
      border: 'solid 1px #e4e7eb',
    },
    '& .MuiInputBase-input': {
      fontFamily: 'NotoSans-Bold',
      fontSize: '1rem',
      letterSpacing: '0',
      color: '#2e4469',
      fontWeight: '500',
      width: '100%',
      height: '3.5rem',
      padding: '0',
      paddingLeft: '1rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'unset',
    },
    '& .MuiIconButton-root': {
      padding: '0',
    },
  }),
  dateIcon: {
    color: '#c0c6cb',
    fontSize: '1.36rem',
  },
  label: {
    margin: '0',
    marginRight: '0.625rem',
    '& .MuiTypography-body1': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: '#2e4469',
    },
  },
  radio: {
    color: '#e4e7eb',
  },
  dialogBodyDatePicker: ({ endDayError }) => ({
    '& .MuiInputBase-root': {
      borderRadius: '0.625rem',
      border: endDayError ? '0.0625rem solid #f44336' : '0.0625rem solid #E4E7EB',
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: '#2e4469',
      fontWeight: 'normal',
      width: '13.5rem',
      height: '3rem',
    },
    '& .MuiButtonBase-root': {
      borderRadius: '0',
      padding: '0',
      '&:hover': {
        backgroundColor: '#ffffff',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      display: 'none',
    },
    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: '0.75rem',
    },
  }),
  Picon: {
    fontSize: '1.375rem',
    color: '#538fff',
  },
  checkBox: {
    '&.MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '&.MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '&.Mui-checked': {
      color: '#28d2ab',
    },
  },
  star: {
    fontSize: '0.875rem',
    color: '#ff0000',
  },
  errorMsg: {
    color: '#f44336',
    margin: '3px 0.825rem 0px',
    fontFamily: 'NotoSans-Medium',
    fontWeight: 500,
  },
}));

const initTextByLanguage = ({ language, item }) => (language === 'en-us' ? item.descEN : item.descCH);

const AddServiceHours = ({ onClose, addServiceHoursApi, date }) => {
  const [endDayError, setEndDayError] = useState(false);
  const classes = useStyles({ endDayError });
  const loginInfo = useSelector((state) => state.loginInfo);
  const language = useSelector((state) => state.settings.language);
  const [dropdownData, setDropdownData] = useState(null);
  const [formData, setFormData] = useState({
    serviceHoursColor: '#BAD2FF',
    startTime: '',
    endTime: '',
    serviceHoursType: 0,
    endDay: null,
    radio: '0',
    durationTime: 30,
    bufferTime: 5,
    wardId: loginInfo.wardId,
    hospitalId: loginInfo.hospitalId,
    startDay: moment(date, 'yyyy-MM-DD'),
    checkedEndDay: false,
  });
  const [addServiceHoursConfirmation, setAddServiceHoursConfirmation] = useState(false);
  const { errorMsg, focusCheck, checkAll, timeCheck } = useServiceHoursCheckRules(formData);

  const handelInputTime = (value) => {
    let inputTime = value;
    if (formData.startTime.length === 1 && inputTime.length === 2) {
      inputTime += ':';
    }
    if (inputTime.length === 3 && inputTime.indexOf(':') === -1) {
      inputTime = `${inputTime.slice(0, 2)}:${inputTime.slice(2, 3)}`;
    }
    if (!/^\\d+$/.test(inputTime)) {
      inputTime = inputTime.replaceAll(/[^0-9:]/g, '');
    }
    if (inputTime.length > 5) {
      inputTime = inputTime.slice(0, 5);
    }
    return inputTime;
  };

  const formHandler = useCallback((key) => {
    switch (key) {
      case 'startDay':
      case 'serviceHoursColor':
      case 'serviceHoursType':
      case 'durationTime':
      case 'bufferTime':
        return (value) => {
          setFormData((preData) => ({ ...preData, [key]: value }));
        };
      case 'startTime':
      case 'endTime':
        return (value) => {
          setFormData((preData) => ({ ...preData, [key]: handelInputTime(value) }));
        };
      case 'radio':
        return (event) => {
          setFormData((preData) => ({ ...preData, [key]: event.target.value }));
        };
      case 'endDay':
        return (value) => {
          setEndDayError(!value);

          setFormData((preData) => ({ ...preData, [key]: value }));
        };
      default:
        break;
    }
  }, []);

  const checkChange = (name) => {
    return (event) => {
      setFormData({ ...formData, [name]: Number(event.target.checked) });
    };
  };

  const itemLists = useMemo(() => {
    let color = [];
    _.map(dropdownData?.staticData, (item, index) => {
      if (item.dataKey === 'ServiceHourColor') {
        color.push({
          text: initTextByLanguage({ language, item }),
          value: item.dataValue,
          color: item.dataValue,
        });
      }
    });

    let repeatPattern = [];
    _.map(dropdownData?.staticData, (item, index) => {
      if (item.dataKey === 'ServiceHourType') {
        repeatPattern.push({
          text: initTextByLanguage({ language, item }),
          value: Number(item.dataValue),
          displaySeq: item.displaySeq,
        });
      }
    });

    repeatPattern = _.sortBy(repeatPattern, ['displaySeq']);

    let durationPerTelevisit = [];
    _.map(dropdownData?.staticData, (item, index) => {
      if (item.dataKey === 'ServiceHourDuartion') {
        durationPerTelevisit.push({
          text: initTextByLanguage({ language, item }),
          value: Number(item.dataValue),
          displaySeq: item.displaySeq,
        });
      }
    });
    durationPerTelevisit = _.sortBy(durationPerTelevisit, ['displaySeq']);

    let bufferTime = [];
    _.map(dropdownData?.staticData, (item, index) => {
      if (item.dataKey === 'ServiceHourBufferTime') {
        bufferTime.push({
          text: initTextByLanguage({ language, item }),
          value: Number(item.dataValue),
        });
      }
    });

    return { color, repeatPattern, durationPerTelevisit, bufferTime };
  }, [dropdownData, language]);

  const confirmHandle = async () => {
    try {
      await checkAll();
      if (formData.checkedEndDay) {
        setEndDayError(!formData.endDay);
        if (!formData.endDay) {
          return;
        }
      }
      setAddServiceHoursConfirmation(true);
    } catch (e) {
      console.error(e);
    }
  };

  // dropdown data
  const staticDataListApi = useCallback(async () => {
    try {
      const { result } = await teleVisitAPI.getStaticDataList();
      setDropdownData(result);
    } catch (error) {}
  }, []);

  useEffect(() => {
    (async () => {
      await staticDataListApi();
    })();
  }, [staticDataListApi]);

  useEffect(() => {
    if (timeCheck(formData.startTime) && timeCheck(formData.endTime)) {
      if (
        moment(formData.endTime, 'HH:mm').diff(moment(formData.startTime, 'HH:mm')) / (1000 * 60) <
        formData.bufferTime + formData.durationTime
      ) {
        formHandler('endTime')('');
      }
    }
  }, [formData.startTime, formData.endTime, formData.bufferTime, formData.durationTime, formHandler]);

  const confirmDisable = useMemo(
    () => !(formData.startTime && formData.endTime),
    [formData.startTime, formData.endTime]
  );

  useEffect(() => {
    if (!formData.checkedEndDay) {
      formHandler('endDay')(null);
      setEndDayError(false);
    }
  }, [formData.checkedEndDay, formHandler]);

  const todayFlag = useMemo(() => moment().isSame(moment(formData.startDay, 'yyyy-MM-DD'), 'D'), [formData.startDay]);

  const closeAddServiceHoursConfirmation = (props) => (e) => {
    setAddServiceHoursConfirmation(props);
  };

  const addServiceHoursConfirmHandle = () => {
    try {
      if (typeof addServiceHoursApi === 'function') {
        addServiceHoursApi({ ...formData, endDay: formData.endDay === null ? 0 : formData.endDay }, onClose);
      }
    } catch (error) {}
  };

  return (
    <>
      {!addServiceHoursConfirmation && (
        <Dialog className={classes.root} open={true}>
          {/* header */}
          <Box display="flex" justifyContent="space-between" mb="1.965rem" p="0 1.875rem">
            <div className={classes.title}>{i18n.t('TELEVISIT_SETTING_POPUP_ADD_SERVICE_HOUR')}</div>
            <ButtonBase className={classes.closeBtn} onClick={onClose}>
              <CloseIcon className={classes.closeIcon} />
            </ButtonBase>
          </Box>
          {/* body */}
          <Box p="0 1.938rem">
            <Box display="flex" justifyContent="space-between" gridColumnGap="1.25rem" mb="1.25rem">
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <div className={classes.inputTitle}>{i18n.t('TELEVISIT_SETTING_POPUP_START_DAY')}</div>
                <KeyboardDatePicker
                  autoOk
                  // id={id}
                  className={classes.date}
                  size="small"
                  value={formData.startDay}
                  onChange={formHandler('startDay')}
                  keyboardIcon={<DateIcon className={classes.dateIcon} />}
                  variant="inline"
                  format="dddd, YYYY-MM-DD"
                  inputVariant="outlined"
                  minDate={moment().format('YYYY-MM-DD')}
                />
              </Box>
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <div className={classes.inputTitle}>{i18n.t('TELEVISIT_SETTING_POPUP_COLOR')} </div>
                <AutoCompleteSelect
                  type="color"
                  itemList={itemLists.color}
                  value={formData.serviceHoursColor}
                  onChange={formHandler('serviceHoursColor')}
                />
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" gridColumnGap="1.25rem" mb="1.875rem">
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <div className={classes.inputTitle}>
                  {i18n.t('TELEVISIT_SETTING_POPUP_START_TIME')} <span className={classes.star}>*</span>{' '}
                </div>
                <TimeInput
                  onBlur={focusCheck('startTime', todayFlag)}
                  placeholder={i18n.t('TELEVISIT_SETTING_POPUP_START_TIME')}
                  value={formData.startTime}
                  onChange={formHandler('startTime')}
                  errorMsg={errorMsg?.startTime}
                  staticData={dropdownData?.staticData}
                  today={todayFlag}
                  inputProps={{ value: formData.startTime }}
                />
              </Box>
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <Box className={classes.inputTitle} display="flex">
                  {i18n.t('TELEVISIT_SETTING_POPUP_END_TIME')}
                  <span className={classes.star}>*</span>
                  <LongRemark
                    content={
                      <div dangerouslySetInnerHTML={{ __html: i18n.t('TELEVISIT_SETTING_POPUP_END_TIME_REMARK') }} />
                    }
                    width="100%"
                    minWidth="10rem"
                    id="longremark-zoom"
                  />
                </Box>
                <TimeInput
                  onBlur={focusCheck('endTime', todayFlag)}
                  errorMsg={errorMsg?.endTime}
                  placeholder={i18n.t('TELEVISIT_SETTING_POPUP_END_TIME')}
                  value={formData.endTime}
                  onChange={formHandler('endTime')}
                  staticData={dropdownData?.staticData}
                  today={todayFlag}
                  inputProps={{ value: formData.endTime }}
                />
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" gridColumnGap="1.25rem" mt="1.25rem" mb="1.875rem">
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <Box className={classes.inputTitle} display="flex">
                  {i18n.t('TELEVISIT_SETTING_POPUP_DURATION')}
                  <LongRemark
                    content={i18n.t('TELEVISIT_SETTING_POPUP_DURATION_REMARK')}
                    width="100%"
                    minWidth="10rem"
                    id="longremark-zoom"
                  />
                </Box>
                <AutoCompleteSelect
                  itemList={itemLists.durationPerTelevisit}
                  value={formData.durationTime}
                  onChange={formHandler('durationTime')}
                />
              </Box>
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <Box className={classes.inputTitle} display="flex">
                  {i18n.t('TELEVISIT_SETTING_POPUP_BUFFER_TIME')}
                  <LongRemark
                    content={i18n.t('TELEVISIT_SETTING_POPUP_BUFFER_TIME_REMARK')}
                    width="100%"
                    minWidth="10rem"
                    id="longremark-zoom"
                  />
                </Box>
                <AutoCompleteSelect
                  itemList={itemLists.bufferTime}
                  value={formData.bufferTime}
                  onChange={formHandler('bufferTime')}
                />
              </Box>
            </Box>

            <div className={classes.subTitle}>{i18n.t('TELEVISIT_SETTING_POPUP_CUSTOM_RECURRENCE')}</div>
            <Box display="flex" justifyContent="space-between" gridColumnGap="1.25rem" mt="1.25rem">
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <div className={classes.inputTitle}>{i18n.t('TELEVISIT_SETTING_POPUP_REPEAT_PATTERN')} </div>
                <AutoCompleteSelect
                  itemList={itemLists.repeatPattern}
                  value={formData.serviceHoursType}
                  onChange={formHandler('serviceHoursType')}
                />
              </Box>
              <Box display="flex" flexBasis="50%" />
            </Box>
            {formData.serviceHoursType !== 0 && (
              <Box display="flex" mt="1.25rem">
                <RadioGroup row onChange={formHandler('radio')} defaultValue="1">
                  <FormControlLabel
                    className={classes.label}
                    value="1"
                    control={
                      <Checkbox
                        className={classes.checkBox}
                        id="DutyAssign-RadioGroup-afb-1"
                        disabled={Boolean(formData.released)}
                      />
                    }
                    checked={Boolean(formData.checkedEndDay)}
                    label={i18n.t('TELEVISIT_SETTING_POPUP_END_DAY')}
                    style={{ marginBottom: '1.75rem' }}
                    labelPlacement="end"
                    onChange={checkChange('checkedEndDay')}
                    // checked={formData.endDay ? true : false}
                  />
                  <Box display="flex" flexDirection="column">
                    <KeyboardDatePicker
                      autoOk
                      className={classes.dialogBodyDatePicker}
                      keyboardIcon={
                        <DateIcon
                          className={classes.Picon}
                          style={{ color: formData.endDay === null ? '#c0c6cb' : '#538fff' }}
                        />
                      }
                      value={formData.endDay}
                      onChange={formHandler('endDay')}
                      id="DutyAssign-KeyboardDatePicker-afd"
                      format="dddd, YYYY-MM-DD"
                      inputVariant="outlined"
                      InputAdornmentProps={{ position: 'end' }}
                      placeholder="Select end date"
                      // disabled={formData.radio !== '1'}
                      disabled={Boolean(formData.checkedEndDay) !== true}
                      disablePast
                      minDate={moment(formData.startDay)}
                      minDateMessage="start day after end day."
                      // minDate={moment(time, 'D/M/YYYY').add(1, 'd').format('YYYY-MM-DD')}
                      // maxDate={moment(time, 'D/M/YYYY').add(365, 'd').format('YYYY-MM-DD')}
                      // onBlur={focusCheck('weeklyEndTime', formData.weeklyEndTime)}
                      variant="inline"
                    />
                    {endDayError && (
                      <div className={classes.errorMsg}>{i18n.t('SETTING_TELEVISIT_SERVICE_HOUR_END_DAY_ERROR')}</div>
                    )}
                  </Box>
                </RadioGroup>
              </Box>
            )}
          </Box>
          {/* footer */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            p="1.125rem"
            mt="1.5rem"
            borderTop="solid 1px #e4e7eb"
            gridColumnGap="1.25rem"
          >
            <StyleButton
              text={i18n.t('SETTING_CANCEL')}
              width="12.5rem"
              height="2.875rem"
              type="fill"
              backgroundColor="#f5faff"
              color="#538fff"
              onClick={onClose}
            />
            <StyleButton
              text={i18n.t('SETTING_CONFIRM')}
              width="12.5rem"
              height="2.875rem"
              type="fill"
              onClick={() => {
                (async () => {
                  await confirmHandle();
                })();
              }}
              disabled={confirmDisable}
            />
          </Box>
        </Dialog>
      )}
      {addServiceHoursConfirmation && (
        <AddServiceHoursConfirmation
          open={addServiceHoursConfirmation}
          onClose={closeAddServiceHoursConfirmation(false)}
          confirmHandle={addServiceHoursConfirmHandle}
        />
      )}
    </>
  );
};

export default AddServiceHours;
